import {isKeyPressed, keyCodes} from '@ey/utils';
import PropTypes from 'prop-types';
import React, {useCallback, useState} from 'react';
import styled from 'styled-components';
import {isValidFunction, labels} from '../../../util/utils';
import FormField from '../BaseComponentWrappers/MotifFormField';
import Input from '../BaseComponentWrappers/MotifInput';
import ProgressBar from '../BaseComponentWrappers/MotifProgressBar';
import CanvasFormInputButtons from './CanvasFormInputButtons';
import Label from '../BaseComponentWrappers/MotifLabel';

function CommonInput({
	disabled,
	id,
	name,
	label,
	maxLength,
	minLength,
	onAccept,
	onBlur,
	onCancel,
	onChange,
	onEdit,
	onFocus,
	pattern,
	placeholder,
	progressBarClassName,
	required,
	type,
	value
}) {
	const [inputValue, setInputValue] = useState(value || ''),
		[isEditing, setIsEditing] = useState(false),
		[isLoading, setIsLoading] = useState(false);

	const handleOnFocus = () => {
		setIsEditing(true);
		if (isValidFunction(onFocus)) {
			onEdit(id);
		}
	};

	const handleOnChange = (event) => {
		let sanitizedInputValue = event.target.value;
		if (pattern) {
			const regexPattern = new RegExp(pattern);
			sanitizedInputValue = sanitizedInputValue.replace(regexPattern, '');
		}
		setInputValue(sanitizedInputValue);
		if (isValidFunction(onChange)) {
			onChange(value, sanitizedInputValue, name);
		}
	};

	const handleOnAccept = useCallback(() => {
		setIsLoading(true);
		if (isValidFunction(onAccept)) {
			onAccept(inputValue)
				.then(() => {
					setIsLoading(false);
					setIsEditing(false);
				})
				.catch(() => {
					setIsLoading(false);
					handleOnCancel();
				});
		}
	}, [inputValue]);

	const handleOnCancel = useCallback(() => {
		setIsEditing(false);
		setInputValue(value);
		if (isValidFunction(onCancel)) {
			onCancel(name);
		}
	}, [value]);

	const handleOnPaste = useCallback(
		(event) => {
			let pastedText = event.clipboardData.getData('text/plain');
			pastedText = pastedText.substring(0, 15);
			if (pattern) {
				const regexPattern = new RegExp(pattern);
				pastedText = pastedText.replace(regexPattern, '');
			}
			setInputValue(pastedText);
			event.preventDefault();
			if (isValidFunction(onChange)) {
				onChange(value, pastedText, name);
			}
		},
		[inputValue]
	);

	const handleOnKeyDown = (event) => {
		if (isKeyPressed(event.nativeEvent, keyCodes.ENTER)) {
			handleOnAccept();
			event.target.blur();
		} else if (isKeyPressed(event.nativeEvent, keyCodes.ESCAPE)) {
			handleOnCancel();
			event.target.blur();
		}
	};
	return (
		<StyledCommonInput
			className={`StyledCommonInput ${inputValue !== '' ? 'common-input-has-value' : ''} ${
				isLoading ? 'StyledCommonInputLoading' : ''
			}`}
		>
			<FormField>
				{isLoading && (
					<ProgressBar isIndeterminate circle hideLabel className={`common-input-loading ${progressBarClassName}`} />
				)}
				<Label position="in" title={label}>
					{label}
				</Label>
				<Input
					autoFocus={false}
					disabled={disabled}
					id={id}
					className={name}
					maxLength={maxLength}
					minLength={minLength}
					onFocus={handleOnFocus}
					onBlur={onBlur}
					onChange={handleOnChange}
					onKeyDown={handleOnKeyDown}
					placeholder={placeholder}
					required={required}
					type={type}
					value={inputValue}
					onPaste={handleOnPaste}
					onClick={handleOnFocus}
					suffix={
						isEditing &&
						!disabled && <CanvasFormInputButtons value={value} onAccept={handleOnAccept} onCancel={handleOnCancel} />
					}
				/>
			</FormField>
		</StyledCommonInput>
	);
}

CommonInput.propTypes = {
	/* Auto focus the input component when it renders. */
	autoFocus: PropTypes.bool,
	/* Class Name will be append to the FormField component. */
	className: PropTypes.string,
	/* Title for the clear button */
	clearButtonTitle: PropTypes.string,
	/* Whether the component is disabled or not. */
	disabled: PropTypes.bool,
	/* Whether the clear button is hidden or not. */
	hideClearButton: PropTypes.bool,
	/* Identifier for the input component. */
	id: PropTypes.string.isRequired,
	/* Whether the input allows editable text. */
	isReadOnly: PropTypes.bool,
	/* Label text for the component. */
	label: PropTypes.string,
	/* Maximum value length. */
	maxLength: PropTypes.number,
	/* Minimum value length. */
	minLength: PropTypes.number,
	/* Event handler for onAccept (click accept button). */
	onAccept: PropTypes.func.isRequired,
	/* Event handler for onBlur. */
	onBlur: PropTypes.func,
	/* Event handler for onCancel (click cancel button). */
	onCancel: PropTypes.func.isRequired,
	/* Event handler for onChange. */
	onChange: PropTypes.func,
	/* Event handler for promise completion. */
	onChanged: PropTypes.func,
	/* Event handler for editing. */
	onEdit: PropTypes.func,
	/* Event handler for onFocus. */
	onFocus: PropTypes.func,
	/* Pattern for the input component. */
	pattern: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(RegExp)]),
	/* Placeholder for the input component. */
	placeholder: PropTypes.string,
	/* ClassName for the progress bar component. */
	progressBarClassName: PropTypes.string,
	/* ClassName for the label component. */
	progressBarLabel: PropTypes.string,
	/* Whether the input is required or not. */
	required: PropTypes.bool,
	/* If true, the progress bar will be shown. */
	showLabel: PropTypes.bool,
	/* If true, the progress bar will be shown. */
	showProgressBar: PropTypes.bool,
	/* Type for the input value. */
	type: PropTypes.oneOf(['date', 'email', 'number', 'password', 'tel', 'text']),
	/* Input value. */
	value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

CommonInput.defaultProps = {
	isReadOnly: false,
	autoFocus: true,
	disabled: false,
	progressBarClassName: 'canvas-form-input-progress-bar',
	progressBarLabel: labels.saving,
	showLabel: false,
	type: 'text'
};

export default CommonInput;

const StyledCommonInput = styled.section`
	display: flex;
	width: var(--px-600);
	height: var(--px-44) !important;
	.motif-form-field {
		.common-input-loading {
			width: 100%;
			height: 100%;
		}
		.StyledCanvasFormInputDisplay {
			border: 0;
			.canvas-form-input-display-value {
				height: 100%;
				padding-top: var(--px-22);
				line-height: var(--px-22);
				color: var(--neutrals-900);
				font-size: var(--px-14);
				font-weight: 300;
				.motif-label {
					text-overflow: ellipsis;
					white-space: nowrap;
					overflow: hidden;
				}
				input {
					height: 100%;
				}
			}
		}
		.motif-input-component {
			.motif-input-right-components {
				height: var(--px-45);
			}
		}
	}
	&.common-input-has-value {
		.motif-form-field {
			.motif-label {
				margin: 0 !important;
			}
			.StyledCanvasFormInputDisplay {
				.motif-label {
					margin: 0 !important;
				}
			}
		}
	}
	&.StyledCommonInputLoading {
		.motif-form-field {
			.motif-progress-bar-wrapper {
				z-index: 2;
				position: absolute;
				.motif-progress-bar {
					width: var(--px-20);
					height: var(--px-20);
				}
			}
		}
	}
`;
